import { useEffect, useMemo } from "react";

import config from "Constants/api_config";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { environmentSelector } from "Reducers/environment";
import getAccessToken from "src/organization/pages/project/environment/metrics/api/getAccessToken";
import { fetchActivitiesInRange } from "src/organization/pages/project/environment/metrics/api/getActivitiesWorker";
import { timeframeToRange } from "src/organization/pages/project/environment/metrics/components/Chart/settings";
import { OVERLAY_ACTIVITY_TYPES } from "src/organization/pages/project/environment/metrics/constant";
import { TimeFrameType } from "src/organization/pages/project/environment/metrics/resources/types";

import { concatenateUrls } from "../utils/concatenateUrls";
import { Poller } from "../utils/polling/poller";

type ActivityPollType = {
  timeframe: TimeFrameType;
  onChange: (v: any) => void;
  onError?: (e: unknown) => void;
  interval?: number;
  enabled?: boolean;
};

const typeArrayToQueryString = (types: string[]) =>
  types.map(type => `type=${type}`).join("&");

const usePollActivities = ({
  timeframe,
  onChange,
  onError,
  enabled,
  interval
}: ActivityPollType) => {
  const environment = useSelectorWithUrlParams(environmentSelector);
  const activityLink = environment?.getLinks()?.["#activities"];

  const poller = useMemo(
    () => new Poller({ onChange, onError }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    const href = activityLink?.href;
    if (!enabled || !href) {
      return;
    }

    const fetchActivities = async () => {
      const { start, end } = timeframeToRange(timeframe);
      const token = await getAccessToken();
      const types = typeArrayToQueryString(OVERLAY_ACTIVITY_TYPES);

      const endpoint = concatenateUrls(config.api_url, href).concat(
        `?${types}`
      );

      return fetchActivitiesInRange(endpoint, token, start, end);
    };

    //time with Id does not have a start which makes It is suitable for continous polling
    timeframe.id
      ? poller.startPolling(fetchActivities, interval)
      : poller.pollOnce(fetchActivities);
  }, [poller, activityLink?.href, timeframe, enabled, interval]);

  useEffect(() => {
    return () => poller.stopPolling();
  }, [poller]);
  return poller;
};

export default usePollActivities;
